import getSessionAuditJSON from '../../../shared/api/sessionAudit/sessionAudit.js';
import { redirectWithGet, redirectWithPost } from '../../../shared/api/redirect/redirect.js';
import { isValidBase64 } from '../../../shared/validators/isValidBase64.js';
import { renderOtpAccess } from '../form/otpAccess/renderOtpAccess.js';
import { getFormLanguage } from '../../../shared/DOM/getFormLanguage.js';

const AUTH_ERROR = {
  PASSWORD_EXPIRED: 7,
  OTP_NOT_CONFIGURED: 35,
  OTP_REQUIRED: 36,
  ENROLLMENT_REQUIRED: 38,
  BATCH_USER: 47,
};

/**
 * RegisterSession.
 *
 * @param {Object} params
 * @param {string} params.user ej 'M_ODENIEL'
 * @param {string} params.password ej 'dsfsdfsd=SDFsdf?dasf_'
 * @param {string} params.domain ej 'M_ODENIEL'
 * @param {string} params.group ej 'ASPEDITESTM'
 * @returns
 */
export async function registerSession({ user, password, domain, group, seccode } = {}) {
  let response;

  const params = {
    user, // ej. "M_ODENIEL",
    password, // ej. "micontraseña",
    domain, // ej. "M_ODENIEL",
    group, // ej. "ASPEDITESTM"
  };

  params.audit = getSessionAuditJSON(params);

  // 06/2023: Permitimos acceso por OTP
  if (seccode) {
    params.seccode = seccode;
  }

  try {
    response = await fetch('/connect/registerSession', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(params),
    });
  } catch (e) {
    console.error(e);
    throw e;
  }

  // Control de errores
  if (!response.ok) {
    const error = await response.json();

    // Gestionados
    if (handleRegisterSessionError(error, params)) {
      return null; // no token + no error
    }

    // El resto acaban en mensaje de error
    throw new Error(error.message || 'login.error_unknown');
  }

  try {
    const data = await response.json();
    const { tokena } = data;
    return tokena;
  } catch (e) {
    console.error(e);
    throw e;
  }
}

/**
 * navitateTo > Errores conocidos y que podemos tratar del registerSession
 *
 * @param {*} error
 * @param {*} params
 * @returns
 */
function handleRegisterSessionError(error, params) {
  let handleError = false;

  switch (error.code) {
    case AUTH_ERROR.OTP_REQUIRED:
      // Protegemos: Si existe params.seccode ya estamos en renderOtpAccess
      // Aunque devolvería OTP_INVALIDCODE: 37
      if (!params.seccode) {
        redirectOtpAccess(params);
        handleError = true;
      }
      break;

    case AUTH_ERROR.OTP_NOT_CONFIGURED:
      redirectWizardOtp(params);
      handleError = true;
      break;

    case AUTH_ERROR.ENROLLMENT_REQUIRED:
      // token required
      if (isValidBase64(error.detail)) {
        redirectEnrollment(params, error.detail);
        handleError = true;
      }
      break;

    case AUTH_ERROR.PASSWORD_EXPIRED:
      // token required
      if (isValidBase64(error.detail)) {
        redirectRenewPwd(params, error.detail);
        handleError = true;
      }
      break;

    default:
    // empty Sonar
  }

  return handleError;
}

// Genera circular dependency pero controlada
export function redirectOtpAccess(params) {
  renderOtpAccess(params);
}

export function redirectWizardOtp(params) {
  const urlParams = getUrlParamsBasic(params);
  redirectWithPost(`/wizardOtp?${urlParams}`);
}

// 04/2024: 'enrollment/eidas' desaparece y es un paso más (13:'EIDAS)
export function redirectEnrollment(params, token) {
  const urlParams = getUrlParamsBasic(params);
  urlParams.set('token', token);
  redirectWithPost(`/enrollment?${urlParams}`);
}

function redirectRenewPwd(params, token) {
  // Importante propagar el search (custom, etc.)
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set('user', params.user);
  urlParams.set('domain', params.domain);
  urlParams.set('group', params.group);
  urlParams.set('expired', true);
  urlParams.set('token', token);

  redirectWithGet(`/renewPwd?${urlParams}`);
}

// Propagamos lo básico
export function getUrlParamsBasic({ user, domain, group, password = '' }) {
  const urlSearch = new URLSearchParams(window.location.search);
  const custom = urlSearch.has('custom') ? urlSearch.get('custom') : 0;
  const lang = urlSearch.has('lang') ? urlSearch.get('lang') : getFormLanguage();

  return new URLSearchParams({ user, domain, group, password, custom, lang });
}
